import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Quote, Copy, Share2, RefreshCw, Check } from 'lucide-react';
import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';

interface Props {
  quote: {
    text: string;
    author: string;
    category?: string;
  };
  onRefresh?: () => void;
}

export function DailyQuote({ quote, onRefresh }: Props) {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyQuote = async () => {
    try {
      await Clipboard.write({
        string: `"${quote.text}" - ${quote.author}`
      });
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Error al copiar:', error);
      try {
        navigator.clipboard.writeText(`"${quote.text}" - ${quote.author}`);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (fallbackError) {
        console.error('Error en fallback de copiar:', fallbackError);
      }
    }
  };

  const handleShareQuote = async () => {
    try {
      await Share.share({
        title: 'Daily Wisdom',
        text: `"${quote.text}" - ${quote.author}`,
        dialogTitle: 'Compartir esta cita'
      });
    } catch (error) {
      console.error('Error al compartir:', error);
      handleCopyQuote();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950
        rounded-2xl shadow-lg border border-blue-800
        p-5 mb-6 overflow-hidden"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#4B6BFD_1px,transparent_1px)] [background-size:14px_14px] pointer-events-none"></div>

      {/* Quote Content */}
      <div className="relative z-10 flex items-start">
        <div className="mr-3">
          <Quote className="w-7 h-7 text-blue-400 flex-shrink-0 mt-1" />
        </div>

        <div className="flex-1">
          <p className="text-lg text-gray-100 font-serif italic mb-2 
            leading-relaxed relative pl-3 border-l border-blue-500">
            {quote.text}
          </p>
          <p className="text-sm text-gray-400 font-medium">
            ― {quote.author}
          </p>
        </div>
      </div>

      {/* Action Buttons - Adaptados al nuevo contenedor */}
      <div className="bottom-4 right-4 flex space-x-4 flex-align-rigth absolute z-10">
        {/* Copy Button */}
        <button
          onClick={handleCopyQuote}
          className={`transition-colors focus:outline-none 
            ${isCopied
              ? 'text-green-400'
              : 'text-gray-500 hover:text-gray-300'
            }
          `}
          aria-label="Copy quote"
        >
          {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>

        {/* Share Button */}
        <button
          onClick={handleShareQuote}
          className="text-gray-500 hover:text-gray-300 transition-colors focus:outline-none"
          aria-label="Share quote"
        >
          <Share2 className="w-4 h-4" />
        </button>

        {/* Refresh Button - Último en la secuencia */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="text-gray-500 hover:text-gray-300 transition-colors focus:outline-none"
            aria-label="Get new quote"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Optional Category */}
      {quote.category && (
        <div className="absolute top-3 right-3 text-xs 
          bg-blue-800/50
          text-blue-300
          px-2 py-0.5 rounded-full">
          {quote.category}
        </div>
      )}
    </motion.div>
  );
}

export default DailyQuote;